import Company from '../models/Company';
import User from '../models/User';
import UserConfigs from '../models/UserConfigs';
import { db } from '../db/db';
import store, { persistor } from '../store';
import { clearCompany, setCompany } from '../store/company/companySlice';
import { removeUser, setUser } from '../store/user/userSlice';
import {
  isZendeskMessagingEnabled,
  ZendeskMessagingCoordinator,
  zendeskMessagingCoordinator
} from './zendeskMessaging';
import {
  ZendeskSupportContextManager,
  zendeskSupportContextManager
} from './zendeskSupportContext';

export interface AuthenticatedIokSession {
  user: User;
  userConfigs: UserConfigs;
  company: Company;
}

export const getAuthenticatedHomePath = (companyType: string): string => {
  if (companyType === 'Analytics') return '/analytics';
  if (companyType === 'Suporte') return '/home/support';
  if (companyType === 'Master') return '/home/master';
  return '/home';
};

export const persistAuthenticatedIokSession = async (
  session: AuthenticatedIokSession
): Promise<void> => {
  await db.transaction('rw', db.user, db.userConfigs, db.company, async () => {
    await Promise.all([db.user.clear(), db.userConfigs.clear(), db.company.clear()]);
    await db.user.put(session.user);
    await db.userConfigs.put(session.userConfigs);
    await db.company.put(session.company);
  });

  store.dispatch(
    setUser({
      user: session.user,
      userConfigs: session.userConfigs
    })
  );
  store.dispatch(setCompany(session.company));
  await persistor.flush();
};

interface CompleteIokLoginDependencies {
  enabled: boolean;
  persist(session: AuthenticatedIokSession): Promise<void>;
  reload(path: string): void;
}

const defaultLoginDependencies: CompleteIokLoginDependencies = {
  enabled: isZendeskMessagingEnabled,
  persist: persistAuthenticatedIokSession,
  reload: (path) => window.location.assign(path)
};

export const completeIokLogin = async (
  session: AuthenticatedIokSession,
  dependencies: Partial<CompleteIokLoginDependencies> = {}
): Promise<void> => {
  const runtime = { ...defaultLoginDependencies, ...dependencies };
  await runtime.persist(session);

  if (runtime.enabled) {
    runtime.reload(getAuthenticatedHomePath(session.company.companyType));
  }
};

export type IokSessionTerminationReason = 'logout' | 'expired' | 'user-change';

export interface IokSessionTerminationOptions {
  reason: IokSessionTerminationReason;
  publicPath?: string;
  beforeClear?: (signal: AbortSignal) => void | Promise<void>;
}

interface IokSessionTerminationDependencies {
  coordinator: Pick<
    ZendeskMessagingCoordinator,
    'markEnding' | 'logoutUserBestEffort' | 'clearIntegrationSession'
  >;
  supportContext?: Pick<ZendeskSupportContextManager, 'clear'>;
  clearIokSession(): Promise<void>;
  navigateToPublic(path: string): void | Promise<void>;
}

export const clearPersistedIokSession = async (): Promise<void> => {
  await db.transaction('rw', db.user, db.userConfigs, db.company, async () => {
    await Promise.all([db.user.clear(), db.userConfigs.clear(), db.company.clear()]);
  });

  store.dispatch(removeUser());
  store.dispatch(clearCompany());
  localStorage.setItem('filters', '');
  localStorage.setItem('searchCookbook', '');
  await persistor.flush();
};

export const createIokSessionTerminator = (
  dependencies: IokSessionTerminationDependencies
) => {
  let activeTermination: Promise<void> | undefined;

  return (options: IokSessionTerminationOptions): Promise<void> => {
    if (activeTermination) return activeTermination;

    const operation = (async () => {
      dependencies.coordinator.markEnding();
      try {
        dependencies.supportContext?.clear();
      } catch {
        // Context cleanup is best-effort and cannot retain the IOK session.
      }
      const controller = new AbortController();
      let timeout: ReturnType<typeof setTimeout> | undefined;
      const remoteCleanup = Promise.allSettled([
        Promise.resolve().then(() => dependencies.coordinator.logoutUserBestEffort()),
        Promise.resolve().then(() => options.beforeClear?.(controller.signal))
      ]);
      // Start both operations before navigating; a stalled remote cannot retain the local session.
      await Promise.race([
        remoteCleanup,
        new Promise<void>((resolve) => {
          timeout = setTimeout(() => {
            controller.abort();
            resolve();
          }, 5000);
        })
      ]);
      clearTimeout(timeout);
      try {
        dependencies.coordinator.clearIntegrationSession();
      } catch {
        // Integration cleanup is best-effort and cannot retain the IOK session.
      }

      try {
        await dependencies.clearIokSession();
      } catch {
        // Navigation remains mandatory even if local cleanup is partially unavailable.
      }

      try {
        await dependencies.navigateToPublic(options.publicPath ?? '/login');
      } catch {
        // A navigation implementation can fail in tests or during page teardown.
      }
    })();

    activeTermination = operation.finally(() => {
      activeTermination = undefined;
    });
    return activeTermination;
  };
};

export const terminateIokSession = createIokSessionTerminator({
  coordinator: zendeskMessagingCoordinator,
  supportContext: zendeskSupportContextManager,
  clearIokSession: clearPersistedIokSession,
  navigateToPublic: (path) => window.location.assign(path)
});
